var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from 'react';
import { InlineVisibilitySelect, Text } from '@ui';
import { REPORT_OPTION_COVER_LETTER, REPORT_OPTION_SUMMARY } from '../constants';
import { effectiveCoverLetter, reportOptionKeys, reportOptionsSummary } from '../utils/reportOptions';
import { useAttachmentFieldCommit } from '../hooks/useAttachmentFieldCommit';
/**
 * Inline-editable `Report options` cell: the two existing booleans, `show_on_report` and
 * `include_in_cover_letter` (plan Decision 3 — the three-value "Cover page, Summary, Letter" label
 * in the original mock was reference art, not a third field).
 *
 * `InlineVisibilitySelect` is a generic multi-checkbox inline select with a summary line; the name
 * is Building's, the component is not Building-specific.
 *
 * Each toggle commits ONE field, matching the single-key PATCH contract the inline cells share —
 * so flipping one box can never clobber the other's value.
 */
export function AttachmentReportOptionsCell({ item, inspectionId, translateFn, hasCoverLetter }) {
    const { commit } = useAttachmentFieldCommit(inspectionId, item.id);
    const [showOnReport, setShowOnReport] = useState(item.showOnReport);
    const [includeInCoverLetter, setIncludeInCoverLetter] = useState(item.includeInCoverLetter);
    const [error, setError] = useState(null);
    const isSavingRef = useRef(false);
    useEffect(() => {
        if (isSavingRef.current)
            return;
        setShowOnReport(item.showOnReport);
        setIncludeInCoverLetter(item.includeInCoverLetter);
    }, [item.showOnReport, item.includeInCoverLetter]);
    const values = reportOptionKeys(showOnReport, includeInCoverLetter);
    // Mirrors the legacy brushaway's `form.showOnReport && hasCoverLetter` gate
    // (Inspections/Show/InspectionAttachments/AttachmentBrushaway.tsx:163). The dependency is real,
    // not cosmetic: `InspectionAttachment.cover_letter_included` is
    // `report_included.where(include_in_cover_letter: true)`, so the flag means nothing on a row
    // that is not on the report at all.
    const showCoverLetterOption = hasCoverLetter && showOnReport;
    const handleChange = (nextValues) => __awaiter(this, void 0, void 0, function* () {
        const nextSummary = nextValues.includes(REPORT_OPTION_SUMMARY);
        const nextCoverLetter = nextValues.includes(REPORT_OPTION_COVER_LETTER);
        isSavingRef.current = true;
        setError(null);
        if (nextSummary !== showOnReport) {
            const result = yield commit({ showOnReport: nextSummary });
            isSavingRef.current = false;
            if (result.success) {
                setShowOnReport(result.row.showOnReport);
                setError(null);
            }
            else {
                setError(result.error);
            }
            return;
        }
        if (nextCoverLetter !== effectiveCoverLetter(showOnReport, includeInCoverLetter)) {
            const result = yield commit({ includeInCoverLetter: nextCoverLetter });
            isSavingRef.current = false;
            if (result.success) {
                setIncludeInCoverLetter(result.row.includeInCoverLetter);
                setError(null);
            }
            else {
                setError(result.error);
            }
            return;
        }
        isSavingRef.current = false;
    });
    return (React.createElement("span", { className: "attachments-surface__report-options-cell", "data-action-cell": true },
        React.createElement(InlineVisibilitySelect, { options: [
                { value: REPORT_OPTION_SUMMARY, label: translateFn('report_options.summary_label') },
                ...(showCoverLetterOption
                    ? [{ value: REPORT_OPTION_COVER_LETTER, label: translateFn('report_options.cover_letter_label') }]
                    : [])
            ], values: values, summary: reportOptionsSummary(showOnReport, includeInCoverLetter, translateFn), onChange: (nextValues) => {
                void handleChange(nextValues);
            } }),
        error && (React.createElement(Text, { variant: "body", role: "alert", className: "attachments-surface__field-error" }, error))));
}
